import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { monitoringAPI } from '../services/api';

const HIDDEN_FIELDS = new Set(['id', 'cdr_id', 'created_at', 'updated_at']);

const TOP_FIELDS = [
  'sip_call_id', 'uuid', 'call_uuid', 'direction', 'switchname',
  'sip_user_agent', 'caller_id_number', 'destination_number',
  'start_epoch', 'answer_epoch', 'end_epoch',
  'duration', 'billsec', 'hangup_cause', 'sip_term_status',
];

function FieldTable({ data, highlight = [] }) {
  if (!data || Object.keys(data).length === 0) {
    return <Typography variant="body2" color="text.secondary">No data</Typography>;
  }

  const entries = Object.entries(data).filter(([k]) => !HIDDEN_FIELDS.has(k));
  const highlightSet = new Set(highlight);

  return (
    <Table size="small">
      <TableBody>
        {entries.map(([key, value]) => (
          <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
            <TableCell
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: highlightSet.has(key) ? 'primary.main' : 'text.secondary',
                width: '35%',
                verticalAlign: 'top',
                py: 0.5,
                pl: 0,
              }}
            >
              {key}
            </TableCell>
            <TableCell
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                wordBreak: 'break-all',
                py: 0.5,
              }}
            >
              {value === null || value === undefined || value === ''
                ? <span style={{ color: '#666' }}>—</span>
                : String(value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function CDRPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cdr, setCdr] = useState(null);

  useEffect(() => {
    if (callId) {
      setLoading(true);
      monitoringAPI.getFullCDR(callId)
        .then(r => setCdr(r.data))
        .catch(e => setError(e.response?.data?.detail || 'Failed to load CDR'))
        .finally(() => setLoading(false));
    }
  }, [callId]);

  const topData = cdr
    ? Object.fromEntries(TOP_FIELDS.filter(f => f in cdr.cdr_records).map(f => [f, cdr.cdr_records[f]]))
    : {};
  const restData = cdr
    ? Object.fromEntries(Object.entries(cdr.cdr_records).filter(([k]) => !TOP_FIELDS.includes(k) && !HIDDEN_FIELDS.has(k)))
    : {};

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
        <Typography variant="h4">SIP / CDR Trace</Typography>
        {cdr && (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {cdr.cdr_records?.sip_call_id || callId}
          </Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {cdr && (
        <>
          {/* Summary strip */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            <Chip label={cdr.cdr_records.direction || 'unknown'} color="primary" variant="outlined" />
            <Chip label={cdr.cdr_records.hangup_cause || 'UNKNOWN'} />
            <Chip label={`${cdr.cdr_records.duration || 0}s`} variant="outlined" />
            {cdr.cdr_rtp?.rtp_use_codec_name && (
              <Chip label={cdr.cdr_rtp.rtp_use_codec_name} variant="outlined" color="secondary" />
            )}
            {cdr.cdr_rtp?.mos && (
              <Chip label={`MOS ${parseFloat(cdr.cdr_rtp.mos).toFixed(2)}`} color="success" variant="outlined" />
            )}
          </Box>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>Core CDR (cdr_records)</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <FieldTable data={topData} highlight={['sip_call_id', 'caller_id_number', 'destination_number', 'hangup_cause']} />
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>All CDR Fields</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <FieldTable data={restData} />
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  RTP / Audio Quality (cdr_rtp)
                </Typography>
                {cdr.cdr_rtp?.mos && (
                  <Chip size="small" label={`MOS ${parseFloat(cdr.cdr_rtp.mos).toFixed(2)}`} color="success" sx={{ ml: 1 }} />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <FieldTable
                  data={cdr.cdr_rtp}
                  highlight={['mos', 'quality_percentage', 'jitter_max_variance', 'rtp_audio_in_jitter_loss_rate', 'rtp_use_codec_name']}
                />
              </AccordionDetails>
            </Accordion>
          </Paper>

          {cdr.cdr_records_extended && Object.keys(cdr.cdr_records_extended).length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>Extended Fields (cdr_records_extended)</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <FieldTable data={cdr.cdr_records_extended} />
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}

          {cdr.cdr_error_periods?.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Error Periods
                    <Chip size="small" label={cdr.cdr_error_periods.length} color="error" sx={{ ml: 1 }} />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  {cdr.cdr_error_periods.map((ep, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                      <FieldTable data={ep} highlight={['flaws', 'consecutive_flaws', 'duration_msec']} />
                      {i < cdr.cdr_error_periods.length - 1 && <Divider sx={{ my: 1 }} />}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
}
